import {
  AccessSettings,
  DialogTitle,
  OutlinedInput,
} from '@aibees/shared/components';
import { PresetVisibility } from '@aibees/shared/definition';
import { Dialog, DialogActions, DialogContent, Fade } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { DialogFooter } from './components/dialog-footer';
import { TemplateColumns } from './components/template-columns';
import { TemplateProvider } from './context';
import * as SX from './sx';
import {
  OperationMode,
  type TemplateManagementDialogProps as Props,
} from './types';

// TODO @mehdi-aibees Please use DialogHeader component with headerDivider prop on the Dialog component

export const TemplateManagementDialog = memo(
  ({
    className: _className,
    open,
    onClose,
    defaultItems,
    dataType,
    mode,
    templateName: _templateName,
    error,
    checkedValues,
    dialogTitle,
    selectedTemplate,
    onBack,
    setSelectedTemplate,
    integrationConfig,
  }: Props) => {
    const [nameError, setNameError] = useState<string>('');
    const [templateName, setTemplateName] = useState<string>('');
    const [visibility, setVisibility] = useState<PresetVisibility>(
      PresetVisibility.INTERNAL
    );

    const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setTemplateName(value);

      // Basic validation
      if (value.length === 0) {
        setNameError('Template Name is required');
      } else if (value.length > 100) {
        setNameError('Limit: 100 characters.');
      } else {
        setNameError('');
      }
    };

    const handleClose = () => {
      onClose();
      setTemplateName('');
      setNameError('');
      setVisibility(PresetVisibility.INTERNAL);
    };
    const handleBack = () => {
      onBack?.();
      setTemplateName('');
      setNameError('');
      setVisibility(PresetVisibility.INTERNAL);
    };

    const handleAccessSettingsSubmit = (
      v: PresetVisibility,
      toggle: boolean
    ) => {
      setVisibility(v);
    };

    useEffect(() => {
      setTemplateName(
        mode === OperationMode.EDIT ? selectedTemplate?.name || '' : ''
      );

      setVisibility(
        mode === OperationMode.EDIT
          ? selectedTemplate?.visibility || PresetVisibility.INTERNAL
          : PresetVisibility.INTERNAL
      );
    }, [mode, selectedTemplate]);

    const accessSettingsItem = useMemo(() => {
      return mode === OperationMode.EDIT
        ? (selectedTemplate as Record<string, any>)
        : undefined;
    }, [mode, selectedTemplate]);

    return (
      <Dialog
        open={open.value}
        fullWidth={true}
        maxWidth="xs"
        TransitionComponent={Fade}
        onClose={handleClose}
        className="TemplateManagementDialog"
      >
        <TemplateProvider
          key={selectedTemplate?.id ?? mode}
          integrationConfig={integrationConfig}
        >
          <DialogTitle
            title={'CSV Layout Settings'}
            icon="chevron-left"
            onClose={handleClose}
            onBack={handleBack}
          />

          <DialogContent sx={SX.dialogContent}>
            {error}

            <OutlinedInput
              label="Export Template Name"
              name="name"
              size="small"
              fullWidth
              placeholder="Enter Template Name"
              value={templateName}
              onChange={handleChangeName}
              required
              error={!!nameError}
              helperText={nameError}
            />

            <AccessSettings
              onSubmit={handleAccessSettingsSubmit}
              item={accessSettingsItem}
            />

            <TemplateColumns
              columns={defaultItems}
              checkedValues={checkedValues}
              integrationConfig={integrationConfig}
            />
          </DialogContent>

          <DialogActions>
            <DialogFooter
              dataType={dataType}
              mode={mode}
              templateName={templateName}
              selectedTemplate={selectedTemplate}
              columns={defaultItems}
              visibility={visibility}
              onClose={handleClose}
              setSelectedTemplate={setSelectedTemplate}
            />
          </DialogActions>
        </TemplateProvider>
      </Dialog>
    );
  }
);

TemplateManagementDialog.displayName = 'TemplateManagementDialog';
